import { Component, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CsPage, NavController } from 'providers/nav-controller';
import { TextDbService, TextEntry } from 'services/text-db.service';
import { ENGLISH_TEXT_SEGMENT, tryHelpIdFromDocId } from 'services/text-locale';
import { LoggingService } from 'services/logging.service';
import { AuthService } from 'services/auth.service';
import { AuthRole } from '@cf-shared/couch-auth';
import { IAdminEditTextPage } from 'pages/admin-edit-text/admin-edit-text-interface';
import { IHelpCardPage } from 'pages/help-card-page/help-card-page-interface';
import { ModalPageMarker } from 'providers/modal-page-marker';

@Component({
    selector: 'page-admin-text-list',
    templateUrl: 'admin-text-list.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ModalPageMarker]
})
export class AdminTextListPage extends CsPage<{}> {
  texts: TextEntry[];
  filteredTexts: TextEntry[];
  searchText = '';

  constructor(
    public navCtrl: NavController,
    protected textDb: TextDbService,
    protected auth: AuthService,
    private cdRef: ChangeDetectorRef,
    private logging: LoggingService,
  ) {
    super();
  }

  async ionViewCanEnter() {
    await this.auth.pageAuthGuardAsync(AuthRole.Dev);
  }
  async ionViewWillEnter() {
    // pin English: this tool edits the AUTHORING documents. The translated
    // siblings are generated into databases/global_common.json and guarded by
    // the drift gate, so they are not hand-edited here.
    this.texts = await this.textDb.getAllTextsAsync(undefined, ENGLISH_TEXT_SEGMENT);
    this.filteredTexts = this.texts;
    this.cdRef.markForCheck();
  }

  handleSearchChanged() {
    let search = this.searchText.toLowerCase();
    this.filteredTexts = this.texts.filter(e =>
      this.formatId(e).toLowerCase().includes(search) ||
      this.formatName(e).toLowerCase().includes(search)
    );
    this.cdRef.markForCheck();
  }

  // called from the template, so it must never throw: a malformed document id
  // renders as an empty cell instead of breaking change detection and taking
  // down the whole list
  formatId(e: TextEntry) {
    let res = tryHelpIdFromDocId(e._id);
    return res === undefined ? '' : res;
  }

  // The id for an ACTION (edit / preview). Unlike formatId, this must not hand
  // back an empty string: admin-edit-text treats a falsy id as "create a new
  // text", so a corrupt row would silently open a blank editor instead of the
  // document the admin tapped. Every id here came from a `@help:` prefix query,
  // so one that does not parse is a corrupt document, not a normal state.
  private helpIdForActionOrReport(e: TextEntry): string | undefined {
    let helpId = tryHelpIdFromDocId(e._id);
    if (helpId === undefined) {
      this.logging.trackSilentProblem('AdminTextListMalformedDocId', { docId: e._id });
    }
    return helpId;
  }
  formatName(e: TextEntry) {
    let res = `${e.title}`;

    return res;
  }

  newText() {
    void this.navCtrl.pushAsync(IAdminEditTextPage, { id: '' });
  }
  editText(e: TextEntry) {
    let helpId = this.helpIdForActionOrReport(e);
    // if the row is corrupt, do nothing rather than opening a blank new-text editor
    if (helpId === undefined) return;
    void this.navCtrl.pushAsync(IAdminEditTextPage, { id: helpId });
  }
  previewText(e: TextEntry) {
    let helpId = this.helpIdForActionOrReport(e);
    if (helpId === undefined) return;
    void this.navCtrl.pushAsync(IHelpCardPage, {
      helpId: '@help:' + helpId,
    });
  }
}
